import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { useContextSystem } from '@wp-g2/context';
import { css, cx, ui } from '@wp-g2/styles';
import { mergeRefs, noop, useResizeAware, useUpdateEffect } from '@wp-g2/utils';
import { useSelect } from 'downshift';
import { useState } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useFormGroupContextId } from '../FormGroup';
import { usePositioner } from '../positioner';
import * as styles from './select-dropdown-styles';
import { getSelectedItem, itemToString, stateReducer } from './select-dropdown-utils';
var CONTROL_BORDER_WIDTH = -1;
var OFFSET = [CONTROL_BORDER_WIDTH, 4];

function useSelectDropdownPositioner(_ref) {
  var _ref$unstable_offset = _ref.unstable_offset,
      unstable_offset = _ref$unstable_offset === void 0 ? OFFSET : _ref$unstable_offset,
      _ref$isOpen = _ref.isOpen,
      isOpen = _ref$isOpen === void 0 ? false : _ref$isOpen,
      otherProps = _objectWithoutProperties(_ref, ["unstable_offset", "isOpen"]);

  var _useResizeAware = useResizeAware(),
      _useResizeAware2 = _slicedToArray(_useResizeAware, 2),
      resizer = _useResizeAware2[0],
      sizes = _useResizeAware2[1];

  var _usePositioner = usePositioner(_objectSpread(_objectSpread({}, otherProps), {}, {
    unstable_offset: OFFSET
  })),
      popoverRef = _usePositioner.popoverRef,
      popper = _usePositioner.popper,
      referenceRef = _usePositioner.referenceRef; // Ensure that the popover will be correctly positioned with an additional
  // update.
  // https://github.com/reakit/reakit/blob/master/packages/reakit/src/Popover/PopoverState.ts


  useEffect(function () {
    if (!isOpen) return undefined;
    var id = window.requestAnimationFrame(function () {
      popper.current.forceUpdate();
    });
    return function () {
      window.cancelAnimationFrame(id);
    };
  }, [isOpen, popper]);
  return {
    resizer: resizer,
    sizes: sizes,
    popoverRef: popoverRef,
    popper: popper,
    referenceRef: referenceRef
  };
}

export function useSelectDropdown(props) {
  var _useContextSystem = useContextSystem(props, 'SelectDropdown'),
      className = _useContextSystem.className,
      error = _useContextSystem.error,
      hideLabelFromVision = _useContextSystem.hideLabelFromVision,
      idProp = _useContextSystem.id,
      _useContextSystem$isP = _useContextSystem.isPreviewable,
      isPreviewable = _useContextSystem$isP === void 0 ? false : _useContextSystem$isP,
      isInline = _useContextSystem.isInline,
      isSubtle = _useContextSystem.isSubtle,
      isOpenProp = _useContextSystem.isOpen,
      label = _useContextSystem.label,
      _useContextSystem$max = _useContextSystem.maxWidth,
      maxWidthProp = _useContextSystem$max === void 0 ? 280 : _useContextSystem$max,
      _useContextSystem$min = _useContextSystem.minWidth,
      minWidth = _useContextSystem$min === void 0 ? 200 : _useContextSystem$min,
      _useContextSystem$onO = _useContextSystem.onOpen,
      onOpen = _useContextSystem$onO === void 0 ? noop : _useContextSystem$onO,
      _useContextSystem$onC = _useContextSystem.onClose,
      onClose = _useContextSystem$onC === void 0 ? noop : _useContextSystem$onC,
      _useContextSystem$onC2 = _useContextSystem.onChange,
      onChange = _useContextSystem$onC2 === void 0 ? noop : _useContextSystem$onC2,
      _useContextSystem$opt = _useContextSystem.options,
      items = _useContextSystem$opt === void 0 ? [] : _useContextSystem$opt,
      placeholder = _useContextSystem.placeholder,
      prefix = _useContextSystem.prefix,
      renderItem = _useContextSystem.renderItem,
      size = _useContextSystem.size,
      suffix = _useContextSystem.suffix,
      textAlign = _useContextSystem.textAlign,
      initialValue = _useContextSystem.value,
      variant = _useContextSystem.variant,
      otherProps = _objectWithoutProperties(_useContextSystem, ["className", "error", "hideLabelFromVision", "id", "isPreviewable", "isInline", "isSubtle", "isOpen", "label", "maxWidth", "minWidth", "onOpen", "onClose", "onChange", "options", "placeholder", "prefix", "renderItem", "size", "suffix", "textAlign", "value", "variant"]);

  var _useState = useState(initialValue),
      _useState2 = _slicedToArray(_useState, 2),
      actualValue = _useState2[0],
      setActualValue = _useState2[1];

  var currentItem = getSelectedItem(items, actualValue);
  /** @type {import('react').MutableRefObject<HTMLSelectElement>} */

  var selectRef = useRef();
  var handleOnChange = useCallback(function (next) {
    onChange(next);
  }, [onChange]);
  var handleOnHighlightChange = useCallback(function (_ref2) {
    var highlightedIndex = _ref2.highlightedIndex;
    if (!isPreviewable) return;
    var item = items[highlightedIndex];
    if (item) onChange({
      selectedItem: item
    });
  }, [isPreviewable, items, onChange]);
  /**
   * Sets up Downshift, the library used for the SelectDropdown component.
   */

  var _useSelect = useSelect({
    initialSelectedItem: items[0],
    items: items,
    isOpen: isOpenProp,
    itemToString: itemToString,
    onHighlightedIndexChange: handleOnHighlightChange,
    onSelectedItemChange: handleOnChange,
    selectedItem: currentItem,
    stateReducer: stateReducer
  }),
      getItemProps = _useSelect.getItemProps,
      getLabelProps = _useSelect.getLabelProps,
      getMenuProps = _useSelect.getMenuProps,
      getToggleButtonProps = _useSelect.getToggleButtonProps,
      highlightedIndex = _useSelect.highlightedIndex,
      isOpen = _useSelect.isOpen,
      selectedItem = _useSelect.selectedItem;

  useEffect(function () {
    if (isPreviewable && isOpen) return;
    setActualValue(initialValue);
  }, [initialValue, isOpen, isPreviewable]);
  /**
   * Sets up DOM render effects.
   */

  var _useSelectDropdownPos = useSelectDropdownPositioner(_objectSpread(_objectSpread({}, otherProps), {}, {
    unstable_offset: OFFSET,
    isOpen: isOpen
  })),
      popoverRef = _useSelectDropdownPos.popoverRef,
      referenceRef = _useSelectDropdownPos.referenceRef,
      resizer = _useSelectDropdownPos.resizer,
      sizes = _useSelectDropdownPos.sizes;

  var id = useFormGroupContextId(idProp);
  var focusSelectButton = useCallback(function () {
    if (selectRef.current) {
      selectRef.current.focus();
    }
  }, []);
  var handleOnOpen = useCallback(function () {
    onOpen();
  }, [onOpen]);
  var handleOnClose = useCallback(function () {
    focusSelectButton();
    onClose();
  }, [focusSelectButton, onClose]);

  var _popoverProps = getMenuProps(_objectSpread(_objectSpread({}, ui.$('SelectDropdownPopover')), {}, {
    className: styles.MenuWrapper,
    'aria-hidden': !isOpen,
    style: {
      maxWidth: isInline ? maxWidthProp : sizes.width,
      width: isInline ? null : '100%'
    }
  })); // We need this here, because the null active descendant is not
  // fully ARIA compliant.


  if (_popoverProps['aria-activedescendant'] && _popoverProps['aria-activedescendant'].slice(0, 'downshift-null'.length) === 'downshift-null') {
    delete _popoverProps['aria-activedescendant'];
  }

  var popoverProps = _objectSpread(_objectSpread({}, _popoverProps), {}, {
    ref: mergeRefs([_popoverProps.ref, popoverRef])
  });

  var _referenceProps = getToggleButtonProps({
    // This is needed because some speech recognition software don't support `aria-labelledby`.
    'aria-label': label,
    'aria-labelledby': undefined
  });

  var referenceProps = _objectSpread(_objectSpread(_objectSpread({}, ui.$('SelectDropdownReference')), _referenceProps), {}, {
    children: itemToString(selectedItem) || placeholder,
    error: error,
    id: id,
    isSubtle: isSubtle,
    prefix: prefix,
    suffix: suffix,
    ref: mergeRefs([_referenceProps.ref, referenceRef, selectRef]),
    size: size,
    textAlign: textAlign,
    variant: variant
  });

  var labelProps = _objectSpread(_objectSpread({}, getLabelProps()), {}, {
    as: 'label',
    children: label
  });

  var enhancedItems = items.map(function (item, index) {
    return _objectSpread(_objectSpread({}, item), {}, {
      index: index,
      renderItem: renderItem
    }, getItemProps({
      item: item,
      index: index,
      key: item.id || item.value || index,
      style: item.style,
      isSelected: item === selectedItem
    }));
  });
  var classes = cx(styles.SelectDropdown, isInline && styles.inline, className);

  var dropdownMenuProps = _objectSpread(_objectSpread({}, ui.$('SelectDropdownMenu')), {}, {
    className: cx(styles.DropdownMenu, css({
      minWidth: minWidth
    }))
  });
  /**
   * Syncs isOpen state (Downshift) with the internal store.
   */


  useUpdateEffect(function () {
    if (isOpen) {
      handleOnOpen();
    } else {
      handleOnClose();
    }
  }, [isOpen]);
  return _objectSpread({
    className: classes,
    dropdownMenuProps: dropdownMenuProps,
    labelProps: labelProps,
    popoverProps: popoverProps,
    resizer: resizer,
    referenceProps: referenceProps,
    items: enhancedItems,
    isOpen: isOpen,
    hideLabelFromVision: hideLabelFromVision
  }, otherProps);
}